import React, { useState } from 'react';
import {
  School,
  EmojiEvents,
  WorkspacePremium,
  ContentPasteSearch,
  More,
  People,
  Stars,
  StickyNote2,
  Work,
  Assistant,
  FactCheck,
  FolderOpen,
} from '@mui/icons-material';

import { Typography } from '@mui/material';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { PAGE_TEXT } from './lang';
import { APPLICATION_MODULE } from './constants';
import { useWithdrawApplication } from '../../graphql';
import {
  type SidebarData,
  Sidebar,
  Button,
  Dialog,
  Alert,
  type AlertDetails,
  ROLES_MODULE,
} from '../../../../components';
import { APPLICATION_STATUS_TYPE } from '../../../../utils';
import { useAuth } from '../../../../contexts';

export type ApplicationCreationSidebarProps = {
  sidebarDisabled: boolean;
  enableSubmit: boolean;
  showSidebar?: boolean;
  isPreview: boolean;
  enableWithdraw: boolean;
  status: string;
  manuallyActivatedId?: string;
  displayRules: Record<string, boolean>;
  onPreviewApplication: () => void;
  onChangeActivated: (activatedId: APPLICATION_MODULE) => void;
  onSubmitApplication: () => void;
  onBack: () => void;
};

const requiredTitle = (name: string): React.ReactNode => {
  return (
    <RequiredTitle>
      {name}
      <span>
        <Stars />
      </span>
    </RequiredTitle>
  );
};

export const ApplicationCreationSidebar: React.FunctionComponent<
  ApplicationCreationSidebarProps
> = props => {
  const { role } = useAuth();
  const isRoleMember = Object.values(ROLES_MODULE).includes(
    role as ROLES_MODULE,
  );

  const {
    status,
    sidebarDisabled,
    manuallyActivatedId,
    displayRules,
    onChangeActivated,
    onSubmitApplication,
    onPreviewApplication,
    onBack,
    enableSubmit,
    isPreview,
    enableWithdraw,
  } = props;

  const {
    displayHKPFS,
    displayResearchExperience,
    displayStudyPlan,
    displayDistinctiveScholarship,
    displayProofDocument,
  } = displayRules;
  const displayApplicationProgress =
    !(status === APPLICATION_STATUS_TYPE.CREATED) && Boolean(status);

  const sidebarData: SidebarData[] = [
    ...(displayApplicationProgress
      ? [
          {
            id: APPLICATION_MODULE.APPLICATION_PROGRESS,
            name: PAGE_TEXT.APPLICATION_PROGRESS,
            icon: <ContentPasteSearch />,
          },
        ]
      : []),
    {
      id: APPLICATION_MODULE.PROGRAMME_CHOICES,
      name: PAGE_TEXT.PROGRAMME.TITLE,
      icon: <School />,
    },
    {
      id: APPLICATION_MODULE.ACADEMIC_REFEREES,
      name: requiredTitle(PAGE_TEXT.OTHER_INFORMATION.ACADEMIC_REFEREE.TITLE),
      icon: <People />,
      disabled: sidebarDisabled,
    },

    ...(displayHKPFS || displayDistinctiveScholarship
      ? [
          {
            id: 'fellowship',
            name: PAGE_TEXT.FELLOWSHIP.TITLE,
            disabled: sidebarDisabled,
            icon: <StickyNote2 />,
            children: [
              ...(displayHKPFS
                ? [
                    {
                      id: APPLICATION_MODULE.HKPFS,
                      name: PAGE_TEXT.FELLOWSHIP.HKPFS.TITLE,
                      disabled: sidebarDisabled,
                    },
                  ]
                : []),
              ...(displayDistinctiveScholarship
                ? [
                    {
                      id: APPLICATION_MODULE.DISTINCTIVE_SCHOLARSHIP,
                      name: PAGE_TEXT.FELLOWSHIP.DISTINCTIVE_SCHOLARSHIP.TITLE,
                      disabled: sidebarDisabled,
                    },
                  ]
                : []),
            ],
          },
        ]
      : []),

    {
      id: 'qualification',
      name: PAGE_TEXT.QUALIFICATION.TITLE,
      disabled: sidebarDisabled,
      icon: <WorkspacePremium />,
      children: [
        {
          id: APPLICATION_MODULE.ACADEMIC_QUALIFICATIONS,
          name: requiredTitle(
            PAGE_TEXT.QUALIFICATION.ACADEMIC_QUALIFICATION.TITLE,
          ),
          disabled: sidebarDisabled,
        },
        {
          id: APPLICATION_MODULE.LANGUAGE_QUALIFICATIONS,
          name: requiredTitle(
            PAGE_TEXT.QUALIFICATION.LANGUAGE_QUALIFICATION.TITLE,
          ),
          disabled: sidebarDisabled,
        },
        {
          id: APPLICATION_MODULE.OTHER_TESTS,
          name: PAGE_TEXT.OTHER_TEST,
          disabled: sidebarDisabled,
        },
      ],
    },

    {
      id: 'partA',
      name: PAGE_TEXT.OTHER_INFORMATION.PART_A,
      disabled: sidebarDisabled,
      icon: <EmojiEvents />,
      children: [
        {
          id: APPLICATION_MODULE.PUBLICATIONS,
          name: PAGE_TEXT.OTHER_INFORMATION.PUBLICATION.TITLE,
          disabled: sidebarDisabled,
        },
        {
          id: APPLICATION_MODULE.MERITS,
          name: PAGE_TEXT.OTHER_INFORMATION.MERIT.TITLE,
          disabled: sidebarDisabled,
        },
        {
          id: APPLICATION_MODULE.PROFESSIONAL_QUALIFICATIONS,
          name: PAGE_TEXT.OTHER_INFORMATION.PROFESSIONAL_QUALIFICATION.TITLE,
          disabled: sidebarDisabled,
        },
        {
          id: APPLICATION_MODULE.TRAINING_EXPERIENCES,
          name: PAGE_TEXT.OTHER_INFORMATION.TRAINING.TITLE,
          disabled: sidebarDisabled,
        },
      ],
    },
    {
      id: 'partB',
      name: PAGE_TEXT.OTHER_INFORMATION.PART_B,
      disabled: sidebarDisabled,
      icon: <Work />,
      children: [
        {
          id: APPLICATION_MODULE.EMPLOYMENT,
          name: PAGE_TEXT.OTHER_INFORMATION.EMPLOYMENT.TITLE,
          disabled: sidebarDisabled,
        },
        {
          id: APPLICATION_MODULE.EXTRA_ACTIVITIES,
          name: PAGE_TEXT.OTHER_INFORMATION.EXTRA_ACTIVITY.TITLE,
          disabled: sidebarDisabled,
        },
      ],
    },

    ...(displayResearchExperience
      ? [
          {
            id: 'partC',
            name: PAGE_TEXT.OTHER_INFORMATION.PART_C,
            disabled: sidebarDisabled,
            icon: <Assistant />,
            children: [
              {
                id: APPLICATION_MODULE.RESEARCH_EXPERIENCES,
                name: requiredTitle(
                  PAGE_TEXT.OTHER_INFORMATION.RESEARCH_EXPERIENCE.TITLE,
                ),
                disabled: sidebarDisabled,
              },
            ],
          },
        ]
      : []),
    {
      id: APPLICATION_MODULE.SUPPORTING_DOCUMENTS,
      name: PAGE_TEXT.ADDITIONAL_DOCUMENTS.SUPPORTING_DOCUMENTS,
      disabled: sidebarDisabled,
      icon: <More />,
      children: [
        {
          id: APPLICATION_MODULE.IDENTIFICATION_DOCUMENT,
          name: requiredTitle(
            PAGE_TEXT.OTHER_INFORMATION.IDENTIFICATION_DOCUMENT.TITLE,
          ),
          disabled: sidebarDisabled,
        },
        {
          id: APPLICATION_MODULE.CURRICULUM_VITAE,
          name: requiredTitle(
            PAGE_TEXT.OTHER_INFORMATION.CURRICULUM_VITAE.TITLE,
          ),
          disabled: sidebarDisabled,
        },
        {
          id: APPLICATION_MODULE.RESEARCH_PROPOSAL,
          name: requiredTitle(
            PAGE_TEXT.OTHER_INFORMATION.RESEARCH_PROPOSAL.TITLE,
          ),
          disabled: sidebarDisabled,
        },
        {
          id: APPLICATION_MODULE.PERSONAL_STATEMENT,
          name: requiredTitle(
            PAGE_TEXT.ADDITIONAL_DOCUMENTS.PERSONAL_STATEMENT,
          ),
          disabled: sidebarDisabled,
        },
        ...(displayStudyPlan
          ? [
              {
                id: APPLICATION_MODULE.STUDY_PLAN,
                name: requiredTitle(
                  PAGE_TEXT.OTHER_INFORMATION.STUDY_PLAN.TITLE,
                ),
                disabled: sidebarDisabled,
              },
            ]
          : []),
        {
          id: APPLICATION_MODULE.ADDITIONAL_DOCUMENTS,
          name: PAGE_TEXT.ADDITIONAL_DOCUMENTS.TITLE,
          disabled: sidebarDisabled,
        },
      ],
    },
    {
      id: APPLICATION_MODULE.DECLARATION,
      name: requiredTitle(PAGE_TEXT.DECLARATION.TITLE),
      disabled: sidebarDisabled,
      icon: <FactCheck />,
    },
    ...(displayProofDocument
      ? [
          {
            id: APPLICATION_MODULE.PROOF_DOCUMENT,
            name: PAGE_TEXT.PROOF_DOCUMENT.TITLE,
            disabled: sidebarDisabled,
            icon: <FolderOpen />,
          },
        ]
      : []),
  ];

  const { applicationId: id } = useParams();
  const [withdrawDialogOpen, setWithdrawDialogOpen] = useState(false);
  const [openAlert, setOpenAlert] = useState(false);
  const [alertDetails, setAlertDetails] = useState<AlertDetails>({
    alertContent: '',
    alertType: 'error',
  });

  const handleChangeActivated = (activatedId: string): void => {
    onChangeActivated(activatedId as APPLICATION_MODULE);
  };

  const withdrawApplication = useWithdrawApplication();
  const handleWithdrawApplication = async (): Promise<void> => {
    try {
      const withdrawApplicationRes = await withdrawApplication({
        variables: { id },
      });
      if (withdrawApplicationRes?.data !== undefined)
        window.location.href = `/application-status/${id}`;
    } catch (e) {
      setAlertDetails({
        alertContent: `Error Occurs.${(e as Error)?.message}`,
        alertType: 'error',
      });
    }
    setOpenAlert(true);
  };

  const handleWithdrawConfirm = async (): Promise<void> => {
    await handleWithdrawApplication();
    setWithdrawDialogOpen(false);
  };

  const onCancelWithdrawApplication = (): void => {
    setWithdrawDialogOpen(false);
  };

  const onWithdrawApplication = (): void => {
    setWithdrawDialogOpen(true);
  };

  const disabledButtonTooltips = isRoleMember
    ? PAGE_TEXT.DISABLED_BUTTON_TOOLTIPS
    : '';
  return (
    <>
      <Dialog
        openDialog={withdrawDialogOpen}
        dialogTitle={PAGE_TEXT.WITHDRAW_CONFIRM.TITLE}
        dialogAction={PAGE_TEXT.WITHDRAW}
        cancelAction={PAGE_TEXT.CANCEL}
        width={'700px'}
        onDialogAction={handleWithdrawConfirm}
        onDialogClose={onCancelWithdrawApplication}
      >
        <Typography margin={'0rem 1.5rem 2rem'}>
          {PAGE_TEXT.WITHDRAW_CONFIRM.CONTENT}
        </Typography>
      </Dialog>
      <Alert
        openAlert={openAlert}
        setOpenAlert={setOpenAlert}
        alertDetails={alertDetails}
      />
      <SidebarViewPoint>
        <Sidebar
          manuallyActivatedId={manuallyActivatedId}
          sidebarData={sidebarData}
          barInnerWidth="18.75rem"
          onChangeActivated={handleChangeActivated}
        />
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            margin: '2rem',
            alignItems: 'center',
          }}
        >
          {enableSubmit &&
            (isPreview ? (
              <Button
                title={disabledButtonTooltips}
                variant="contained"
                onClick={onSubmitApplication}
                disabled={isRoleMember}
                sx={{
                  marginBottom: '1rem',
                  minWidth: '11rem',
                }}
              >
                {PAGE_TEXT.SUBMIT}
              </Button>
            ) : (
              <Button
                title={disabledButtonTooltips}
                variant="contained"
                onClick={onPreviewApplication}
                disabled={isRoleMember}
                sx={{
                  marginBottom: '1rem',
                  minWidth: '11rem',
                }}
              >
                {PAGE_TEXT.PREVIEW}
              </Button>
            ))}
          <Button
            variant="outlined"
            onClick={onBack}
            sx={{
              minWidth: '11rem',
            }}
          >
            {PAGE_TEXT.BACK}
          </Button>
          {enableWithdraw && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                marginTop: '1rem',
              }}
            >
              <Button
                variant="text"
                size="small"
                style={{
                  width: 'fit-content',
                  alignSelf: 'center',
                  textTransform: 'none',
                  marginBottom: '1rem',
                }}
                title={disabledButtonTooltips}
                disabled={isRoleMember}
                onClick={onWithdrawApplication}
              >
                {PAGE_TEXT.WITHDRAW}
              </Button>
            </div>
          )}
        </div>
      </SidebarViewPoint>
    </>
  );
};

export const RequiredTitle = styled.div`
  span {
    height: 100%;
  }
  svg {
    margin-bottom: 0.8rem;
    margin-left: 0.1rem;
    font-size: 0.7rem;
  }
`;

export const SidebarViewPoint = styled.div`
  position: sticky;
  top: 5rem;
  padding-top: 1rem;
  height: calc(100vh - 5rem);
  scroll-behavior: smooth;
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: thin;
  scrollbar-color: #888 #f1f1f1;
  transition: padding-top 0.25s ease;
  -ms-overflow-style: none;
`;
